import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { creatorService, Creator } from '../../services/creator.service';
// Removed mock data import - using only real API data

// Use Creator interface from service, but extend for display purposes
interface Designer extends Creator {
  // Additional fields for display compatibility
  url?: string; // Will map from portfolioImages[0]
  title?: string; // Will map from name or bio
  creator?: string; // Will map from name
  caption?: string; // Will map from bio
  saves?: number; // Default value for compatibility
  height?: string; // Default height for masonry
  description?: string; // For DesignerPostCard compatibility
}

interface DesignersState {
  designers: Designer[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
  retryCount: number;
  lastRetryTime: number | null;
}

const initialState: DesignersState = {
  designers: [],
  isLoading: false,
  error: null,
  lastFetched: null,
  retryCount: 0,
  lastRetryTime: null,
};

// Constants for retry logic
const MAX_RETRIES = 3;
const RETRY_COOLDOWN = 5 * 60 * 60 * 1000; // 5 hours in milliseconds

// Async thunk to fetch designers with retry logic
export const fetchDesigners = createAsyncThunk(
  'designers/fetchDesigners',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as { designers: DesignersState };
    const { retryCount, lastRetryTime } = state.designers;

    // Check if we've exceeded retry limit and are in cooldown
    if (retryCount >= MAX_RETRIES && lastRetryTime) {
      const timeSinceLastRetry = Date.now() - lastRetryTime;
      if (timeSinceLastRetry < RETRY_COOLDOWN) {
        return rejectWithValue(`Too many failed attempts. Please wait ${Math.ceil((RETRY_COOLDOWN - timeSinceLastRetry) / (60 * 60 * 1000))} hours before trying again.`);
      }
    }

    // No mock data - using only real API data

    try {
      const response = await creatorService.getCreators({ limit: 100 });
      
      // Transform creators to designers format for compatibility
      const apiDesigners: Designer[] = response.items
        .map((creator, index) => ({
          ...creator,
          // Map fields for DesignerPostCard compatibility
          url: creator.portfolioImages?.[0] || creator.profilePicture || '',
          title: creator.name,
          creator: creator.name,
          caption: creator.bio && creator.bio.trim().length > 0 ? creator.bio : '', // Use bio if exists, empty string if not
          description: creator.bio && creator.bio.trim().length > 0 ? creator.bio : '', // Use bio if exists, empty string if not
          saves: Math.floor(Math.random() * 1000) + 100, // Placeholder
          height: ['h-64', 'h-72', 'h-80', 'h-88', 'h-96'][index % 5], // Vary heights for masonry
          isVerified: creator.isVerified,
          createdAt: new Date().toISOString(),
        }));

      // Return only API data
      return apiDesigners;
    } catch (error: any) {
      // Return empty array on error
      return [];
    }
  }
);

const designersSlice = createSlice({
  name: 'designers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    // Reset retry count (called on successful fetch or manual reset)
    resetRetryCount: (state) => {
      state.retryCount = 0;
      state.lastRetryTime = null;
    },
    // Action to add a new designer (for when new posts are created)
    addDesigner: (state, action: PayloadAction<Designer>) => {
      state.designers.unshift(action.payload);
      // Reset cache timestamp to trigger refresh
      state.lastFetched = null;
    },
    // Action to update a designer
    updateDesigner: (state, action: PayloadAction<Designer>) => {
      const index = state.designers.findIndex(d => d.id === action.payload.id);
      if (index !== -1) {
        state.designers[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDesigners.pending, (state) => {
        // Only set loading if we're not in cooldown
        const MAX_RETRIES = 3;
        const RETRY_COOLDOWN = 5 * 60 * 60 * 1000; // 5 hours
        if (state.retryCount >= MAX_RETRIES && state.lastRetryTime) {
          const timeSinceLastRetry = Date.now() - state.lastRetryTime;
          if (timeSinceLastRetry < RETRY_COOLDOWN) {
            return; // Don't set loading during cooldown
          }
        }
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDesigners.fulfilled, (state, action) => {
        state.isLoading = false;
        state.designers = action.payload;
        state.lastFetched = Date.now();
        state.error = null;
        state.retryCount = 0; // Reset on success
        state.lastRetryTime = null;
      })
      .addCase(fetchDesigners.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.retryCount += 1;
        state.lastRetryTime = Date.now();
      });
  },
});

export const { clearError, resetRetryCount, addDesigner, updateDesigner } = designersSlice.actions;
export default designersSlice.reducer;
